/**
 * R-06 — the retailer's Orders list filters. They live in the address (`?q=&status=&from=&to=`) so a
 * filtered view survives opening an order and coming back, and can be bookmarked. The list is the account's whole
 * set of orders (it is not paged), so they apply here, together, over everything.
 */
export interface OrderFilters { q: string; status: string; from: string; to: string }

export const NO_FILTERS: OrderFilters = { q: '', status: '', from: '', to: '' };

const KEYS = ['q', 'status', 'from', 'to'] as const;

export const filtersFromParams = (p: URLSearchParams): OrderFilters => ({
  q: p.get('q') ?? '', status: p.get('status') ?? '',
  from: p.get('from') ?? '', to: p.get('to') ?? '',
});

/** Only the filters in use, so a clean list has a clean address. */
export const paramsFromFilters = (f: OrderFilters): Record<string, string> =>
  Object.fromEntries(KEYS.filter((k) => f[k].trim() !== '').map((k) => [k, f[k].trim()]));

export const isFiltering = (f: OrderFilters): boolean => KEYS.some((k) => f[k].trim() !== '');

/** A timestamp's calendar day in the viewer's zone — the day the list shows — as `YYYY-MM-DD`. */
export const localDay = (iso: string): string => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export interface Filterable { reference: string; items: string[]; status: string; submittedAt: string }

/** Every filter in use must match; the text matches the order number or any item name, ignoring case. */
export function filterOrders<T extends Filterable>(orders: readonly T[], f: OrderFilters): T[] {
  const needle = f.q.trim().toLowerCase();
  return orders.filter((o) => {
    if (needle && !o.reference.toLowerCase().includes(needle) && !o.items.some((n) => n.toLowerCase().includes(needle))) return false;
    if (f.status && o.status !== f.status) return false;
    const day = localDay(o.submittedAt);
    if (f.from && day < f.from) return false;
    if (f.to && day > f.to) return false;
    return true;
  });
}
